import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GameDayTile } from "../components/coach/GameDayTile";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, TrendingDown, Minus, ListTodo, AlertCircle,
  Plus, CheckCircle2, Circle, Trash2, Calendar, User, X as XIcon, Search,
  ArrowUpDown, Flag, Users as UsersIcon, ClipboardCheck, DollarSign, Settings, Pencil, ChevronDown, ChevronRight,
  BookMarked, Sunrise, Sunset, Coffee, Dumbbell, Save as SaveIcon, Brain, Zap, HeartHandshake, Film,
  CreditCard, Activity, PanelLeftClose, PanelLeftOpen, LayoutGrid, Contact, Crosshair, Video, Clapperboard, Library as LibraryIcon,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { CoachSessionsSection } from "../components/sessions/CoachSessions";
import { ConfirmPaymentDialog, PaymentSettingsPanel } from "../components/payments/PaymentAdmin";
import { VideoLink } from "../components/VideoModal";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { trainingLabel } from "../lib/training";
import UsersActivitySection from "../components/UsersActivitySection";
import CoachDatabaseSection from "../components/CoachDatabaseSection";

const LEAGUE_LABEL = { NBA: "NBA", NCAA: "NCAA M", WNCAA: "NCAA W", WNBA: "WNBA", PRO: "PRO", HS: "HS" };
const PRIORITY_ORDER = { high: 0, med: 1, low: 2 };
const PRIORITY_STYLE = {
  high: { color: "#f87171", label: "HIGH" },
  med:  { color: "#f59e0b", label: "MED"  },
  low:  { color: "#60a5fa", label: "LOW"  },
};

function fmtRelDays(days) {
  if (days === null || days === undefined) return "Never";
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 10);
  } catch { return iso; }
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

export default function CoachDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section") || "tasks";
  const setSection = (k) => setSearchParams({ section: k });
  const [navOpen, setNavOpen] = useState(() => { const v = localStorage.getItem("coachNavOpen"); return v === null ? window.innerWidth >= 1024 : v !== "0"; });
  useEffect(() => { localStorage.setItem("coachNavOpen", navOpen ? "1" : "0"); }, [navOpen]);
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [trending, setTrending] = useState(null);

  // Master table controls
  const [query, setQuery] = useState("");
  const [leagueFilter, setLeagueFilter] = useState("ALL");
  const [sortKey, setSortKey] = useState("last_touch_days");
  const [sortDir, setSortDir] = useState("desc");

  // Task drawer
  const [taskDrawer, setTaskDrawer] = useState(false);
  const [taskFilter, setTaskFilter] = useState("open");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("med");
  const [newTaskPlayerId, setNewTaskPlayerId] = useState("");
  const [allTasks, setAllTasks] = useState([]);
  const [savingTask, setSavingTask] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, tr] = await Promise.all([
        api.coachDashboard(21),
        api.trending(5).catch(() => null),
      ]);
      setDashboard(dash);
      setTrending(tr);
    } catch (e) {
      toast.error("Failed to load dashboard", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const params = taskFilter === "all" ? {} : { status: taskFilter };
      const r = await api.listCoachTasks(params);
      setAllTasks(r.tasks || []);
    } catch { toast.error("Failed to load tasks"); }
  }, [taskFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (taskDrawer) loadTasks(); }, [taskDrawer, loadTasks]);

  const filteredPlayers = useMemo(() => {
    if (!dashboard?.players) return [];
    let rows = dashboard.players;
    if (leagueFilter !== "ALL") rows = rows.filter(p => p.league === leagueFilter);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter(p => (p.name || "").toLowerCase().includes(q) || (p.team || "").toLowerCase().includes(q));
    const dir = sortDir === "asc" ? 1 : -1;
    const key = sortKey;
    // For any *_days column, treat null (never touched) as the stalest possible value so those
    // rows sink to the bottom on ascending sort and rise to the top on descending sort.
    const DAYS_KEYS = new Set(["last_touch_days"]);
    const val = (r) => {
      const v = r[key];
      if (v === null || v === undefined || v === "") return DAYS_KEYS.has(key) ? 999999 : "";
      return v;
    };
    return [...rows].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [dashboard, leagueFilter, query, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const addTask = async () => {
    if (!newTaskTitle.trim()) { toast.error("Title required"); return; }
    setSavingTask(true);
    try {
      await api.createCoachTask({
        title: newTaskTitle.trim(),
        due_date: newTaskDue || null,
        priority: newTaskPriority,
        player_id: newTaskPlayerId || null,
      });
      setNewTaskTitle(""); setNewTaskDue(""); setNewTaskPriority("med"); setNewTaskPlayerId("");
      await Promise.all([loadTasks(), loadAll()]);
      toast.success("Task added");
    } catch (e) { toast.error("Failed to add", { description: e?.response?.data?.detail || e.message }); }
    finally { setSavingTask(false); }
  };

  const toggleTaskDone = async (t) => {
    try {
      await api.updateCoachTask(t.id, { status: t.status === "done" ? "open" : "done" });
      await Promise.all([loadTasks(), loadAll()]);
    } catch { toast.error("Update failed"); }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.deleteCoachTask(t.id);
      await Promise.all([loadTasks(), loadAll()]);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  // Toggle handlers — refresh dashboard after each change so counts stay accurate.
  // Kept ABOVE the loading early-return so React Hook order stays stable across renders.
  const toggleOnboardingItem = useCallback(async (p, item) => {
    const isDone = !!(p.onboarding && p.onboarding[item.key]);
    setDashboard(d => ({
      ...d,
      players: d.players.map(x => {
        if (x.id !== p.id) return x;
        const cur = { ...(x.onboarding || {}) };
        if (isDone) delete cur[item.key]; else cur[item.key] = { done_at: new Date().toISOString(), done_by_email: "you" };
        return { ...x, onboarding: cur, onboarding_done_count: Object.keys(cur).length };
      }),
    }));
    try {
      if (isDone) await api.markOnboardingUndone(p.id, item.key);
      else await api.markOnboardingDone(p.id, item.key);
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
      loadAll();
    }
  }, [loadAll]);

  const togglePaid = useCallback(async (p) => {
    const currentMonth = dashboard?.current_month;
    if (!currentMonth) return;
    const wasPaid = !!p.current_month_paid;
    setDashboard(d => ({
      ...d,
      players: d.players.map(x => x.id === p.id
        ? { ...x, current_month_paid: !wasPaid, current_month_paid_at: !wasPaid ? new Date().toISOString() : null }
        : x),
    }));
    try {
      if (wasPaid) await api.markUnpaid(p.id, currentMonth.year, currentMonth.month);
      else await api.markPaid(p.id, currentMonth.year, currentMonth.month);
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
      loadAll();
    }
  }, [dashboard, loadAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh] text-[#a0a0a0]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading dashboard…
        </div>
      </div>
    );
  }

  const { tasks = { open_count: 0, overdue: [], upcoming: [] }, onboarding_items = [], current_month = null } = dashboard || {};
  const monthLabel = current_month ? new Date(current_month.year, current_month.month - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" }).toUpperCase() : "MONTH";

  const SECTIONS = [
    { key: "tasks",    label: "Tasks",            Icon: ListTodo,     badge: tasks.open_count || 0 },
    { key: "routines", label: "Routine Library",  Icon: BookMarked, href: "/routine-library" },
    { key: "sessions", label: "Zoom Sessions",    Icon: Video },
    { key: "library",  label: "Library",          Icon: LibraryIcon, href: "/library" },
    { key: "filmdb", label: "Film Database", Icon: Film, href: "/film-database" },
    { key: "players",  label: "Player Database",  Icon: UsersIcon },
    { key: "payments", label: "Payments",         Icon: CreditCard },
    { key: "users",    label: "Users & Activity", Icon: Activity },
    ...(canEdit ? [{ key: "coachdb", label: "Coach Database", Icon: Contact }] : []),
  ];
  const current = SECTIONS.find(x => x.key === section) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Sidebar */}
        <aside
          className={`shrink-0 border-r border-white/10 bg-[#0f0f0f]/90 backdrop-blur-xl sticky top-14 sm:top-16 self-start h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] flex flex-col ${navOpen ? "w-60" : "w-14"}`}
          style={{ transition: "width .25s cubic-bezier(.16,1,.3,1)" }}
          data-testid="coach-sidebar"
        >
          <button
            onClick={() => setNavOpen(o => !o)}
            className="flex items-center gap-3 h-12 px-4 border-b border-white/10 text-[#a0a0a0] hover:text-white"
            data-testid="coach-sidebar-toggle"
            title={navOpen ? "Collapse menu" : "Expand menu"}
          >
            {navOpen ? <PanelLeftClose className="w-4 h-4 shrink-0" /> : <PanelLeftOpen className="w-4 h-4 shrink-0" />}
            {navOpen && <span className="text-[10px] uppercase tracking-[0.35em] font-bold">Command Center</span>}
          </button>
          <nav className="flex-1 py-2">
            {SECTIONS.map(({ key, label, Icon, badge, href }) => {
              const active = section === key;
              return (
                <button
                  key={key}
                  onClick={() => href ? navigate(href) : setSection(key)}
                  title={label}
                  className={`relative w-full flex items-center gap-3 h-11 px-4 text-left ${active ? "text-white bg-[#3aa0ff]/10" : "text-[#a0a0a0] hover:text-white hover:bg-white/[0.04]"}`}
                  data-testid={`coach-nav-${key}`}
                >
                  {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-[#3aa0ff] shadow-[0_0_10px_#3aa0ff]" />}
                  <Icon className={`w-4 h-4 shrink-0 ${active ? "text-[#3aa0ff]" : ""}`} />
                  {navOpen && <span className="text-[11px] uppercase tracking-[0.25em] font-bold truncate">{label}</span>}
                  {badge > 0 && (
                    <span className={`${navOpen ? "ml-auto" : "absolute top-1.5 right-1.5"} bg-[var(--org-accent)] text-[#121212] text-[9px] font-bold px-1.5 py-0.5 leading-none`} data-testid={`coach-nav-${key}-badge`}>{badge}</span>
                  )}
                </button>
              );
            })}
          </nav>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 h-11 px-4 border-t border-white/10 text-[#a0a0a0] hover:text-white"
            data-testid="go-player-list-btn"
            title="Player List"
          >
            <LayoutGrid className="w-4 h-4 shrink-0" />
            {navOpen && <span className="text-[11px] uppercase tracking-[0.25em] font-bold">Player List</span>}
          </button>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-8 space-y-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)]">Coach's Command Center</div>
              <h1 className="font-heading text-4xl lg:text-5xl uppercase tracking-tight text-white mt-1" data-testid="coach-section-title">{current.label}</h1>
              <div className="text-[12px] text-[#a0a0a0] mt-2">
                {dashboard?.players?.length || 0} players tracked · touchpoint window {dashboard?.touchpoint_days || 21} days
              </div>
            </div>
            {section === "tasks" && (
              <button
                onClick={() => setTaskDrawer(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[11px] uppercase tracking-[0.3em] font-bold"
                data-testid="open-tasks-btn"
              >
                <ListTodo className="w-3.5 h-3.5" /> Manage Tasks
                {tasks.open_count > 0 && <span className="ml-1 bg-[#0a0a0a] text-[var(--org-accent)] px-1.5 py-0.5 text-[10px]">{tasks.open_count}</span>}
              </button>
            )}
          </div>

          {section === "tasks" && <GameDayTile />}
          {section === "tasks" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TasksTile tasks={tasks} onOpenDrawer={() => setTaskDrawer(true)} />
            </div>
          )}
          {section === "sessions" && <CoachSessionsSection />}
          {section === "players" && (
          <section className="border border-white/10 bg-[#141414]" data-testid="master-table">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Player Database</div>
                <div className="font-heading text-xl uppercase tracking-tight text-white">All Tracked Players</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setTemplateOpen(true)}
                  className="flex items-center gap-1.5 px-3 h-9 text-[10px] uppercase tracking-[0.3em] font-bold border border-white/15 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
                  data-testid="onboarding-template-btn"
                >
                  <Settings className="w-3 h-3" /> Onboarding Items
                </button>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#757575]" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search…"
                    className="pl-8 pr-3 h-9 bg-[#0f0f0f] border border-white/10 text-xs text-white focus:outline-none focus:border-[var(--org-accent)] w-56"
                    data-testid="master-search"
                  />
                </div>
                <select
                  value={leagueFilter}
                  onChange={(e) => setLeagueFilter(e.target.value)}
                  className="h-9 bg-[#0f0f0f] border border-white/10 px-3 text-xs text-white"
                  data-testid="master-league-filter"
                >
                  {["ALL", "NBA", "NCAA", "WNCAA", "WNBA", "PRO", "HS"].map(l => <option key={l} value={l}>{l === "ALL" ? "All Leagues" : LEAGUE_LABEL[l] || l}</option>)}
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-[#757575] bg-[#141414] border-b border-white/10">
                    {onboarding_items.map(item => (
                      <Th key={item.key}>
                        <span className="inline-flex items-center gap-1 text-emerald-400"><ClipboardCheck className="w-3 h-3" /> {item.label}</span>
                      </Th>
                    ))}
                    <Th sortable onClick={() => toggleSort("name")}       active={sortKey === "name"}       dir={sortDir} sticky>Player</Th>
                    <Th>League</Th>
                    <Th>Tier</Th>
                    <Th sortable onClick={() => toggleSort("last_touch_days")} active={sortKey === "last_touch_days"} dir={sortDir}>Last Touch</Th>
                    <Th sortable onClick={() => toggleSort("on_court_count")} active={sortKey === "on_court_count"} dir={sortDir}>On Court</Th>
                    <Th sortable onClick={() => toggleSort("virtual_count")} active={sortKey === "virtual_count"} dir={sortDir}>Virtual</Th>
                    <Th>Recruiting</Th>
                    <Th>Trend</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlayers.map((p) => {
                    const trend = trending?.by_id?.[p.id];
                    return (
                      <tr
                        key={p.id}
                        onClick={() => navigate(`/home/${p.id}`)}
                        className="group border-b border-white/5 cursor-pointer"
                        data-testid={`master-row-${p.id}`}
                      >
                        {onboarding_items.map(item => {
                          const done = !!(p.onboarding && p.onboarding[item.key]);
                          return (
                            <td key={item.key} className="px-3 py-3 text-center group-hover:bg-white/[0.03]" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => toggleOnboardingItem(p, item)}
                                title={`${item.label}${done ? ` · done ${new Date(p.onboarding[item.key].done_at).toLocaleDateString()}` : ""}`}
                                className={`w-5 h-5 border-2 flex items-center justify-center mx-auto ${done ? "" : "hover:border-white/40"}`}
                                style={done
                                  ? { background: "#22c55e", borderColor: "#22c55e" }
                                  : { background: "#0f0f0f", borderColor: "#333" }}
                                data-testid={`onboard-${p.id}-${item.key}`}
                              >
                                {done && <CheckCircle2 className="w-3 h-3 text-[#121212]" strokeWidth={3} />}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-white font-bold sticky left-0 z-10 bg-[#141414] group-hover:bg-[#242424]">{p.name}<div className="text-[10px] text-[#757575] font-normal">{p.team || "—"}</div></td>
                        <td className="px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] group-hover:bg-white/[0.03]">{LEAGUE_LABEL[p.league] || p.league}</td>
                        <td className="px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] group-hover:bg-white/[0.03]">{trainingLabel(p.training_level, p.training_sub_tier, { compact: true })}</td>
                        <td className={`px-4 py-3 text-[12px] group-hover:bg-white/[0.03] ${p.last_touch_days === null ? "text-[#f87171]" : (p.last_touch_days >= 21 ? "text-[#f59e0b]" : "text-[#a0a0a0]")}`}>
                          {fmtRelDays(p.last_touch_days)}
                        </td>
                        <td className={`px-4 py-3 text-[12px] group-hover:bg-white/[0.03] ${(p.on_court_count || 0) === 0 ? "text-[#575757]" : "text-white font-bold"}`}>
                          {p.on_court_count || 0}
                          <span className="text-[10px] text-[#575757] uppercase tracking-widest ml-1">sess</span>
                        </td>
                        <td className={`px-4 py-3 text-[12px] group-hover:bg-white/[0.03] ${(p.virtual_count || 0) === 0 ? "text-[#575757]" : "text-white font-bold"}`}>
                          {p.virtual_count || 0}
                          <span className="text-[10px] text-[#575757] uppercase tracking-widest ml-1">mtgs</span>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-[#a0a0a0]">
                          {p.recruitment_stage ? <span>{p.recruitment_stage}<span className="text-[#575757]"> · {p.recruitment_school || "—"}</span></span> : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {trend ? <TrendPill trend={trend} /> : <span className="text-[#575757] text-[11px]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPlayers.length === 0 && (
                    <tr><td colSpan={onboarding_items.length + 8} className="text-center py-12 text-[#757575] text-sm">No players match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
  
            )}
          {section === "payments" && (
        <PaymentsSection
            players={dashboard?.players || []}
            currentMonth={current_month}
            onChanged={loadAll}
          />
            )}
          {section === "users" && <UsersActivitySection />}
          {section === "coachdb" && canEdit && <CoachDatabaseSection />}
        </main>
      </div>

      {taskDrawer && (
        <TaskDrawer
          onClose={() => setTaskDrawer(false)}
          allTasks={allTasks}
          taskFilter={taskFilter}
          setTaskFilter={setTaskFilter}
          onAdd={addTask}
          savingTask={savingTask}
          newTaskTitle={newTaskTitle}       setNewTaskTitle={setNewTaskTitle}
          newTaskDue={newTaskDue}           setNewTaskDue={setNewTaskDue}
          newTaskPriority={newTaskPriority} setNewTaskPriority={setNewTaskPriority}
          newTaskPlayerId={newTaskPlayerId} setNewTaskPlayerId={setNewTaskPlayerId}
          players={dashboard?.players || []}
          onToggle={toggleTaskDone}
          onDelete={deleteTask}
          canEdit={canEdit}
        />
      )}
      {templateOpen && (
        <OnboardingTemplateDrawer
          initialItems={onboarding_items}
          onClose={() => setTemplateOpen(false)}
          onSaved={() => { setTemplateOpen(false); loadAll(); }}
        />
      )}
    </div>
  );
}

/* ----------------- Sub-components ----------------- */

function Th({ children, sortable, onClick, active, dir, sticky }) {
  return (
    <th
      className={`px-4 py-3 text-left font-bold ${sortable ? "cursor-pointer hover:text-white select-none" : ""} ${sticky ? "sticky left-0 z-10 bg-[#141414]" : ""}`}
      onClick={onClick}
    >
      <span className={active ? "text-[var(--org-accent)]" : ""}>{children}</span>
      {sortable && <ArrowUpDown className={`w-3 h-3 inline ml-1 ${active ? "text-[var(--org-accent)]" : "text-[#575757]"}`} />}
    </th>
  );
}

function TrendPill({ trend }) {
  const dir = trend.direction || "flat";
  const style = dir === "up"   ? { color: "#4ade80", Icon: TrendingUp }
              : dir === "down" ? { color: "#f87171", Icon: TrendingDown }
              :                  { color: "#a0a0a0", Icon: Minus };
  const { Icon, color } = style;
  const sign = trend.delta_pts > 0 ? "+" : "";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color }}>
      <Icon className="w-3 h-3" />{sign}{trend.delta_pts} pts
    </span>
  );
}

function TasksTile({ tasks, onOpenDrawer }) {
  const overdue = tasks.overdue || [];
  const upcoming = tasks.upcoming || [];
  return (
    <div className="tech-tile border border-white/10 bg-[#141414] flex flex-col" data-testid="tile-tasks">
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
        <ListTodo className="w-4 h-4 text-[#60a5fa]" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#60a5fa]">Tasks</div>
          <div className="font-heading text-lg uppercase text-white leading-tight">Open · {tasks.open_count || 0}</div>
        </div>
        <button
          onClick={onOpenDrawer}
          className="ml-auto text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] hover:text-white"
          data-testid="tile-open-task-drawer"
        >
          Manage →
        </button>
      </div>
      <div className="p-5 space-y-3 flex-1">
        {overdue.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <AlertCircle className="w-3.5 h-3.5 text-[#f87171]" />
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#f87171]">Overdue</div>
            </div>
            <div className="space-y-1.5">
              {overdue.map(t => <TaskCompactRow key={t.id} t={t} />)}
            </div>
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#a0a0a0] mb-2">Upcoming</div>
          {upcoming.length === 0 ? (
            <div className="text-[12px] italic text-[#575757] py-2">Nothing on deck.</div>
          ) : (
            <div className="space-y-1.5">
              {upcoming.map(t => <TaskCompactRow key={t.id} t={t} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskCompactRow({ t }) {
  const du = daysUntil(t.due_date);
  const dueLabel = du === null ? "" : du < 0 ? `${-du}d late` : du === 0 ? "Today" : du === 1 ? "Tomorrow" : `in ${du}d`;
  const dueColor = du === null ? "#757575" : du < 0 ? "#f87171" : du <= 2 ? "#f59e0b" : "#a0a0a0";
  const pri = PRIORITY_STYLE[t.priority || "med"];
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-[#101010]">
      <Flag className="w-3 h-3 shrink-0" style={{ color: pri.color }} />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-white truncate">{t.title}</div>
        {t.player_name && <div className="text-[10px] text-[#757575] truncate">{t.player_name}</div>}
      </div>
      <span className="text-[10px] font-bold shrink-0" style={{ color: dueColor }}>{dueLabel}</span>
    </div>
  );
}

/* ----------------- Task drawer ----------------- */

function TaskDrawer({
  onClose, allTasks, taskFilter, setTaskFilter, onAdd, savingTask,
  newTaskTitle, setNewTaskTitle, newTaskDue, setNewTaskDue,
  newTaskPriority, setNewTaskPriority, newTaskPlayerId, setNewTaskPlayerId,
  players, onToggle, onDelete, canEdit,
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <aside
        className="fixed right-0 top-0 h-full w-full sm:w-[520px] bg-[#141414] border-l border-white/10 z-50 flex flex-col"
        data-testid="task-drawer"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Coach's Tasks</div>
            <div className="font-heading text-xl uppercase text-white">To-Do List</div>
          </div>
          <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="task-drawer-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {canEdit && (
          <div className="px-5 py-4 border-b border-white/10 bg-[#141414] space-y-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1">Add task</div>
            <input
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onAdd()}
              placeholder="Task title (e.g. Call UConn coach)"
              className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
              data-testid="new-task-title"
            />
            <div className="grid grid-cols-3 gap-2">
              <input
                type="date"
                value={newTaskDue}
                onChange={(e) => setNewTaskDue(e.target.value)}
                className="bg-[#0f0f0f] border border-white/10 px-2 py-2 text-xs text-white"
                data-testid="new-task-due"
              />
              <select
                value={newTaskPriority}
                onChange={(e) => setNewTaskPriority(e.target.value)}
                className="bg-[#0f0f0f] border border-white/10 px-2 py-2 text-xs text-white"
                data-testid="new-task-priority"
              >
                <option value="low">Low</option>
                <option value="med">Med</option>
                <option value="high">High</option>
              </select>
              <select
                value={newTaskPlayerId}
                onChange={(e) => setNewTaskPlayerId(e.target.value)}
                className="bg-[#0f0f0f] border border-white/10 px-2 py-2 text-xs text-white"
                data-testid="new-task-player"
              >
                <option value="">No player</option>
                {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <button
              onClick={onAdd}
              disabled={savingTask || !newTaskTitle.trim()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[var(--org-accent)] text-[#121212] text-[11px] uppercase tracking-[0.3em] font-bold disabled:opacity-50"
              data-testid="new-task-submit"
            >
              {savingTask ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Add Task
            </button>
          </div>
        )}

        <div className="px-5 py-3 border-b border-white/10 flex gap-2">
          {["open", "done", "all"].map(f => (
            <button
              key={f}
              onClick={() => setTaskFilter(f)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold ${
                taskFilter === f ? "bg-white/10 text-white" : "text-[#a0a0a0] hover:text-white"
              }`}
              data-testid={`task-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="task-list">
          {allTasks.length === 0 && (
            <div className="text-center py-12 text-[#757575] text-sm italic">
              {taskFilter === "done" ? "No completed tasks yet." : "No tasks. Add one above."}
            </div>
          )}
          {allTasks.map(t => (
            <TaskRow key={t.id} t={t} onToggle={onToggle} onDelete={onDelete} canEdit={canEdit} />
          ))}
        </div>
      </aside>
    </>
  );
}

function TaskRow({ t, onToggle, onDelete, canEdit }) {
  const du = daysUntil(t.due_date);
  const dueLabel = du === null ? "No due date" : du < 0 ? `${-du}d overdue` : du === 0 ? "Due today" : du === 1 ? "Due tomorrow" : `Due in ${du}d`;
  const dueColor = du === null ? "#757575" : du < 0 ? "#f87171" : du <= 2 ? "#f59e0b" : "#a0a0a0";
  const pri = PRIORITY_STYLE[t.priority || "med"];
  const done = t.status === "done";
  return (
    <div className={`border border-white/10 bg-[#101010] p-3 flex items-start gap-3 ${done ? "opacity-60" : ""}`} data-testid={`task-row-${t.id}`}>
      <button
        onClick={() => canEdit && onToggle(t)}
        disabled={!canEdit}
        className="mt-0.5 shrink-0"
        data-testid={`task-toggle-${t.id}`}
      >
        {done ? <CheckCircle2 className="w-5 h-5 text-[#4ade80]" /> : <Circle className="w-5 h-5 text-[#a0a0a0] hover:text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-bold text-white ${done ? "line-through" : ""}`}>{t.title}</div>
        {t.description && <div className="text-[11px] text-[#a0a0a0] mt-1">{t.description}</div>}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: pri.color }}>
            <Flag className="w-3 h-3 inline mr-1" />{pri.label}
          </span>
          <span className="text-[10px] font-bold" style={{ color: dueColor }}>
            <Calendar className="w-3 h-3 inline mr-1" />{dueLabel}{t.due_date ? ` · ${t.due_date}` : ""}
          </span>
          {t.player_name && (
            <span className="text-[10px] text-[#a0a0a0]"><User className="w-3 h-3 inline mr-1" />{t.player_name}</span>
          )}
        </div>
      </div>
      {canEdit && (
        <button onClick={() => onDelete(t)} className="text-[#575757] hover:text-red-400 shrink-0" data-testid={`task-delete-${t.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function OnboardingTemplateDrawer({ initialItems, onClose, onSaved }) {
  const [items, setItems] = useState(() => (initialItems || []).map(i => ({ key: i.key, label: i.label })));
  const [saving, setSaving] = useState(false);

  const addRow = () => setItems(v => [...v, { key: "", label: "" }]);
  const removeRow = (idx) => setItems(v => v.filter((_, i) => i !== idx));
  const updateRow = (idx, label) => setItems(v => v.map((it, i) => i === idx ? { ...it, label } : it));
  const moveRow = (idx, dir) => setItems(v => {
    const next = v.slice();
    const target = idx + dir;
    if (target < 0 || target >= next.length) return v;
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });

  const save = async () => {
    const cleaned = items.map(i => ({ key: i.key || "", label: (i.label || "").trim() })).filter(i => i.label);
    if (cleaned.length === 0) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      await api.saveOnboardingTemplate(cleaned);
      toast.success("Onboarding items saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose} data-testid="onboarding-template-drawer">
      <div className="w-full max-w-lg h-full bg-[#0a0a0a] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Configure</div>
            <div className="font-heading text-xl uppercase tracking-tight text-white">Onboarding Items</div>
          </div>
          <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="onboarding-template-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[12px] text-[#a0a0a0] leading-relaxed">
            These are the steps every new player goes through when they sign up.
            Add or remove items and reorder them — every existing player's checklist updates too.
          </div>
          <ul className="space-y-2">
            {items.map((it, idx) => (
              <li key={idx} className="flex items-center gap-2 bg-[#141414] border border-white/10 p-2">
                <div className="flex flex-col">
                  <button onClick={() => moveRow(idx, -1)} disabled={idx === 0} className="text-[#575757] hover:text-white disabled:opacity-30">▲</button>
                  <button onClick={() => moveRow(idx, +1)} disabled={idx === items.length - 1} className="text-[#575757] hover:text-white disabled:opacity-30">▼</button>
                </div>
                <input
                  value={it.label}
                  onChange={(e) => updateRow(idx, e.target.value)}
                  placeholder="Item name"
                  className="flex-1 bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                  data-testid={`onboarding-item-${idx}`}
                />
                <button onClick={() => removeRow(idx)} className="p-1.5 text-[#757575] hover:text-red-400" title="Remove" data-testid={`onboarding-remove-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={addRow}
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.3em] text-[var(--org-accent)] hover:text-white"
            data-testid="onboarding-add-item"
          >
            <Plus className="w-3 h-3" /> Add item
          </button>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/10">
            <button onClick={onClose} className="px-3 py-2 text-[11px] uppercase tracking-[0.3em] text-[#a0a0a0] hover:text-white">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-[0.3em] font-bold text-[#121212] disabled:opacity-50"
              style={{ background: "var(--org-accent)" }}
              data-testid="onboarding-template-save"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Payments section — separate from the master player table.
 * One row per player. Left columns: Monthly Fee, Start Date (both editable inline).
 * Right side: horizontally-scrolling grid of month checkboxes from start_date → current month.
 * Optimistic UI on toggle + billing edit; refetches on error.
 */
function PaymentsSection({ players, currentMonth, onChanged }) {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  // Draft state (only used in edit mode). Structure:
  //   billingDraft[playerId] = { monthly_fee: string, start_date: string }
  //   paymentsDraft[playerId] = Set of "year-month" strings currently marked paid
  const [billingDraft, setBillingDraft] = useState({});
  const [paymentsDraft, setPaymentsDraft] = useState({});
  const [confirm, setConfirm] = useState(null); // { player, year, month, record }

  // Compute the list of months to display: from earliest start_date across all players → current month.
  const now = new Date();
  const cur = currentMonth || { year: now.getFullYear(), month: now.getMonth() + 1 };
  const months = useMemo(() => {
    let earliest = null;
    for (const p of players) {
      const sd = p.billing_start_date;
      if (!sd) continue;
      const d = new Date(sd + "T00:00:00");
      if (isNaN(d)) continue;
      if (!earliest || d < earliest) earliest = d;
    }
    if (!earliest) earliest = new Date(cur.year, cur.month - 6, 1);
    const list = [];
    let y = earliest.getFullYear();
    let m = earliest.getMonth() + 1;
    while (y < cur.year || (y === cur.year && m <= cur.month)) {
      list.push({ year: y, month: m });
      m += 1;
      if (m === 13) { m = 1; y += 1; }
      if (list.length > 60) break;
    }
    return list;
  }, [players, cur.year, cur.month]);

  const sorted = useMemo(() => {
    const order = { "Maximize": 0, "The Program Platinum": 1, "Workouts": 2, "WME NIL": 3 };
    return [...players].sort((a, b) => {
      const ao = order[a.training_level] ?? 4;
      const bo = order[b.training_level] ?? 4;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [players]);

  // Dues recur on the day-of-month the player started; status per month → paid / reported / overdue / due / upcoming.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dueDateFor = (p, y, m) => {
    if (!p.billing_start_date) return null;
    const day = Number(p.billing_start_date.slice(8, 10)) || 1;
    return new Date(y, m - 1, Math.min(day, new Date(y, m, 0).getDate()));
  };
  const recordFor = (p, y, m) => (p.payments || []).find(x => x.year === y && x.month === m);
  const statusFor = (p, y, m) => {
    const r = recordFor(p, y, m);
    if (r && (r.status || "paid") === "paid") return "paid";
    if (r) return "reported";
    const due = dueDateFor(p, y, m);
    if (!due) return "due";
    if (due > today) return "upcoming";
    return "overdue";
  };
  const summary = useMemo(() => {
    const out = { expected: 0, collected: 0, reported: 0, overdue: 0, dueSoon: [] };
    for (const p of players) {
      if (!p.monthly_fee || !p.billing_start_date || p.billing_start_date > `${cur.year}-${String(cur.month).padStart(2, "0")}-31`) continue;
      out.expected += Number(p.monthly_fee);
      const st = statusFor(p, cur.year, cur.month);
      if (st === "paid") out.collected += Number(p.monthly_fee);
      else if (st === "reported") out.reported += 1;
      else if (st === "overdue") out.overdue += 1;
      else { const d = dueDateFor(p, cur.year, cur.month); if (d && (d - today) / 86400000 <= 3) out.dueSoon.push(p.name); }
    }
    return out;
  }, [players, cur.year, cur.month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed draft state from live data whenever entering edit mode.
  const enterEdit = () => {
    const bd = {}, pd = {};
    for (const p of players) {
      bd[p.id] = {
        monthly_fee: p.monthly_fee == null ? "" : String(p.monthly_fee),
        start_date:  p.billing_start_date || "",
      };
      pd[p.id] = new Set((p.payments || []).map(x => `${x.year}-${x.month}`));
    }
    setBillingDraft(bd);
    setPaymentsDraft(pd);
    setEditMode(true);
  };
  const cancelEdit = () => {
    setBillingDraft({}); setPaymentsDraft({}); setEditMode(false);
  };

  // ---- Draft mutations (edit mode only) ----
  const setDraftBilling = (pid, patch) => setBillingDraft(prev => ({
    ...prev,
    [pid]: { ...(prev[pid] || { monthly_fee: "", start_date: "" }), ...patch },
  }));
  const toggleDraftPaid = (pid, y, m) => setPaymentsDraft(prev => {
    const cur = new Set(prev[pid] || []);
    const k = `${y}-${m}`;
    if (cur.has(k)) cur.delete(k); else cur.add(k);
    return { ...prev, [pid]: cur };
  });

  // ---- Save: compute a minimal diff and hit the API for every changed row ----
  const save = async () => {
    setSaving(true);
    try {
      const jobs = [];
      for (const p of players) {
        // Billing diff
        const d = billingDraft[p.id];
        if (d) {
          const feeStr = (d.monthly_fee ?? "").trim();
          const fee = feeStr === "" ? null : Number(feeStr);
          if (fee != null && (isNaN(fee) || fee < 0)) {
            throw new Error(`Invalid fee for ${p.name}`);
          }
          const start = d.start_date || null;
          const feeChanged   = (fee ?? null) !== (p.monthly_fee ?? null);
          const startChanged = (start || "") !== (p.billing_start_date || "");
          if (feeChanged || startChanged) {
            jobs.push(api.updatePlayerBilling(p.id, { monthly_fee: fee, start_date: start }));
          }
        }
        // Payments diff
        const before = new Set((p.payments || []).map(x => `${x.year}-${x.month}`));
        const after = paymentsDraft[p.id] || new Set();
        for (const k of after) if (!before.has(k)) {
          const [y, m] = k.split("-").map(Number);
          jobs.push(api.markPaid(p.id, y, m));
        }
        for (const k of before) if (!after.has(k)) {
          const [y, m] = k.split("-").map(Number);
          jobs.push(api.markUnpaid(p.id, y, m));
        }
      }
      if (jobs.length === 0) { toast.info("Nothing changed"); setEditMode(false); return; }
      await Promise.all(jobs);
      toast.success(`${jobs.length} change${jobs.length === 1 ? "" : "s"} saved`);
      setEditMode(false);
      onChanged();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <section className="border border-white/10 bg-[#141414]" data-testid="payments-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setSectionOpen(v => !v)}
          className="flex items-center gap-3 text-left flex-1 min-w-0 group"
          data-testid="payments-section-toggle"
          aria-expanded={sectionOpen}
        >
          {sectionOpen
            ? <ChevronDown className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0" />
            : <ChevronRight className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0" />}
          <DollarSign className="w-4 h-4 text-emerald-400 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Payments</div>
            <div className="font-heading text-xl uppercase tracking-tight text-white">Monthly Payment Tracker</div>
          </div>
        </button>
        {sectionOpen && (
          editMode ? (
            <div className="flex items-center gap-2">
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#a0a0a0] hover:text-white"
                data-testid="payments-cancel"
              >
                <XIcon className="w-3 h-3 inline mr-0.5" /> Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#121212] disabled:opacity-50"
                style={{ background: "#22c55e" }}
                data-testid="payments-save"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Save
              </button>
            </div>
          ) : (
            <button
              onClick={enterEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold border border-white/15 text-white hover:border-emerald-400 hover:text-emerald-400"
              data-testid="payments-edit"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )
        )}
      </div>
      {sectionOpen && <PaymentSettingsPanel />}
      {sectionOpen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border-b border-white/10" data-testid="payments-summary">
          {[
            ["Collected this month", `$${summary.collected.toLocaleString()} / $${summary.expected.toLocaleString()}`, "#22c55e", "payments-summary-collected"],
            ["Reported · to confirm", summary.reported, "#f59e0b", "payments-summary-reported"],
            ["Overdue", summary.overdue, summary.overdue ? "#ef4444" : "#757575", "payments-summary-overdue"],
            ["Due in next 3 days", summary.dueSoon.length ? summary.dueSoon.join(", ") : "—", "#d8b854", "payments-summary-due-soon"],
          ].map(([k, v, c, tid]) => (
            <div key={k} className="bg-[#141414] px-4 py-3 min-w-0" data-testid={tid}>
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">{k}</div>
              <div className="font-heading text-xl truncate" style={{ color: c }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {sectionOpen && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-[#757575] bg-[#141414] border-b border-white/10">
                <th className="px-4 py-3 text-left sticky left-0 bg-[#141414] z-10">Player</th>
                <th className="px-3 py-3 text-left">Monthly Fee</th>
                <th className="px-3 py-3 text-left">Start Date</th>
                {months.map(({ year, month }) => (
                  <th
                    key={`${year}-${month}`}
                    className={`px-2 py-3 text-center min-w-[56px] ${year === cur.year && month === cur.month ? "text-emerald-400" : ""}`}
                  >
                    {new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "short" })}<br/>
                    <span className="text-[9px] opacity-70">'{String(year).slice(-2)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={3 + months.length} className="text-center py-12 text-[#757575] text-sm">No players.</td></tr>
              )}
              {sorted.map(p => {
                const draft = billingDraft[p.id];
                const feeDisplay = p.monthly_fee == null ? "—" : `$${Number(p.monthly_fee).toFixed(0)}`;
                const startDisplay = p.billing_start_date
                  ? new Date(p.billing_start_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                  : "—";
                return (
                  <tr key={p.id} className="border-b border-white/5" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3 text-white font-bold sticky left-0 bg-[#141414] z-10">
                      {p.name}
                      <div className="text-[10px] text-[#757575] font-normal">{trainingLabel(p.training_level, p.training_sub_tier, { compact: true })}</div>
                    </td>
                    <td className="px-3 py-3">
                      {editMode ? (
                        <div className="flex items-center gap-1">
                          <span className="text-[#757575] text-xs">$</span>
                          <input
                            type="number" min="0" step="1"
                            value={draft?.monthly_fee ?? ""}
                            onChange={(e) => setDraftBilling(p.id, { monthly_fee: e.target.value })}
                            placeholder="—"
                            className="w-20 bg-[#0f0f0f] border border-white/10 px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                            data-testid={`payment-fee-${p.id}`}
                          />
                        </div>
                      ) : (
                        <div className="text-sm text-white">{feeDisplay}</div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {editMode ? (
                        <input
                          type="date"
                          value={draft?.start_date ?? ""}
                          onChange={(e) => setDraftBilling(p.id, { start_date: e.target.value })}
                          className="bg-[#0f0f0f] border border-white/10 px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                          data-testid={`payment-start-${p.id}`}
                        />
                      ) : (
                        <div className="text-sm text-white">{startDisplay}</div>
                      )}
                    </td>
                    {months.map(({ year, month }) => {
                      const k = `${year}-${month}`;
                      const rec = recordFor(p, year, month);
                      const status = editMode
                        ? ((paymentsDraft[p.id] && paymentsDraft[p.id].has(k)) ? "paid" : "due")
                        : (p.billing_start_date && `${year}-${String(month).padStart(2, "0")}-31` < p.billing_start_date ? "na" : statusFor(p, year, month));
                      const paid = status === "paid";
                      const isCurrent = year === cur.year && month === cur.month;
                      const palette = { paid: { background: "#22c55e", borderColor: "#22c55e" }, reported: { background: "#f59e0b33", borderColor: "#f59e0b" }, overdue: { background: "#ef444422", borderColor: "#ef4444" }, upcoming: { background: "#0f0f0f", borderColor: isCurrent ? "#22c55e88" : "#333" }, due: { background: "#0f0f0f", borderColor: isCurrent ? "#22c55e88" : "#333" }, na: { background: "transparent", borderColor: "#222" } };
                      const titles = { paid: `Paid${rec?.method ? ` · ${rec.method}` : ""}${rec?.amount ? ` · $${rec.amount}` : ""}${rec?.note ? ` · ${rec.note}` : ""}`, reported: `Family reported sent${rec?.reported_at ? ` ${new Date(rec.reported_at).toLocaleDateString()}` : ""} — click to confirm`, overdue: "Past due — click to mark paid", upcoming: "Upcoming — click to mark paid", due: "Click to mark paid", na: "Before start date" };
                      return (
                        <td key={k} className="px-2 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => editMode ? toggleDraftPaid(p.id, year, month) : (status !== "paid" && status !== "na" && setConfirm({ player: p, year, month, record: rec }))}
                            disabled={!editMode && (status === "paid" || status === "na")}
                            className={`w-6 h-6 border-2 flex items-center justify-center mx-auto ${paid || status === "na" ? "" : "hover:border-white/60"} ${!editMode && paid ? "cursor-default" : ""}`}
                            style={palette[status]}
                            title={titles[status]}
                            data-status={status}
                            data-testid={`payment-cell-${p.id}-${year}-${month}`}
                          >
                            {paid && <CheckCircle2 className="w-3.5 h-3.5 text-[#121212]" strokeWidth={3} />}
                            {status === "reported" && <span className="text-[10px] font-black text-amber-300">?</span>}
                            {status === "overdue" && <span className="text-[10px] font-black text-red-400">!</span>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-4 px-5 py-3 border-t border-white/5 text-[10px] uppercase tracking-[0.2em] text-[#757575]" data-testid="payments-legend">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2" style={{ background: "#22c55e", borderColor: "#22c55e" }} /> Paid</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2" style={{ background: "#f59e0b33", borderColor: "#f59e0b" }} /> Reported by family</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2" style={{ background: "#ef444422", borderColor: "#ef4444" }} /> Overdue</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2" style={{ background: "#0f0f0f", borderColor: "#333" }} /> Not yet due</span>
            <span className="ml-auto normal-case tracking-normal">Click any unpaid month to confirm with method + amount</span>
          </div>
        </div>
      )}
      {confirm && <ConfirmPaymentDialog {...confirm} onClose={() => setConfirm(null)} onSaved={onChanged} />}
    </section>
  );
}


/* ---------- Routine Templates: tile + drawer ---------- */

function IQLibraryTile() {
  const [db, setDb] = useState(null);
  useEffect(() => { let live = true;
    api.filmDatabase()
      .then(r => {
        if (!live) return;
        const map = {};
        for (const i of (r?.items || [])) if (i.has_video) map[i.library] = (map[i.library] || 0) + 1;
        setDb({ map, total: r?.with_video || 0, tags: r?.tags?.length || 0 });
      })
      .catch(() => { if (live) setDb({ map: {}, total: 0, tags: 0 }); });
    return () => { live = false; };
  }, []);
  const entries = db ? Object.entries(db.map).sort((a, b) => b[1] - a[1]).slice(0, 4) : [];

  return (
    <section className="tech-tile border border-white/10 bg-[#141414] p-5" data-testid="iq-library-tile">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-[var(--org-accent)]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Film Database</div>
            <div className="font-heading text-lg uppercase tracking-tight text-white">Every video · search by tag</div>
          </div>
        </div>
        {db && (
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]" data-testid="iq-library-tile-count">{db.total} videos</span>
        )}
      </div>
      <div className="space-y-2 min-h-[128px]">
        {entries.length === 0 ? (
          <div className="text-xs text-[#575757] italic">No videos yet. Add film in any library and it appears here.</div>
        ) : entries.map(([lib, n]) => (
          <div key={lib} className="flex items-center justify-between px-3 py-2 bg-[#0f0f0f] border border-white/5">
            <span className="text-[11px] uppercase tracking-[0.25em] text-white truncate">{lib}</span>
            <span className="text-xs font-mono text-white">{n}</span>
          </div>
        ))}
      </div>
      <Link to="/film-database"
        className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 border border-[var(--org-accent)]/40 text-[var(--org-accent)] text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:bg-[var(--org-accent)]/5"
        data-testid="iq-library-open-film-db"
      >
        <Brain className="w-3 h-3" /> Open Film Database
      </Link>
    </section>
  );
}
